import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addDays, subDays, parseISO } from "date-fns";
import { enUS } from "date-fns/locale";
import { CalendarRange, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { textColorFor } from "@/lib/format";
import { Card, LabeledSelect } from "@/components/dashboard/Ui";

const localizer = dateFnsLocalizer({
  format, parse, startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 1 }), getDay, locales: { "en-US": enUS },
});

/** Therapists with no specialty set still need to be visible. */
const NO_SPECIALTY = "#94A3B8";

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Read-only calendar of every active therapist's availability, coloured by
 * specialty. Blocks are produced server-side from each therapist's weekly
 * pattern with date exceptions already applied.
 */
export function AvailabilityCalendar() {
  const { data: meta } = useMeta();
  const specialties = useMemo(() => meta?.specialties || {}, [meta]);

  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [specialty, setSpecialty] = useState("");
  const [person, setPerson] = useState("");
  const [range, setRange] = useState({
    start: subDays(startOfMonth(new Date()), 7),
    end: addDays(endOfMonth(new Date()), 7),
  });

  const { data, isFetching } = useQuery({
    queryKey: ["availability-calendar", dateKey(range.start), dateKey(range.end), specialty],
    queryFn: async () =>
      (
        await api.get("/availability/calendar", {
          params: { start: dateKey(range.start), end: dateKey(range.end), specialty: specialty || undefined },
        })
      ).data,
    keepPreviousData: true,
  });

  const onRangeChange = useCallback((r) => {
    if (Array.isArray(r) && r.length) setRange({ start: r[0], end: r[r.length - 1] });
    else if (r?.start && r?.end) setRange({ start: r.start, end: r.end });
  }, []);

  const colorFor = useCallback(
    (spec) => specialties[spec]?.color || NO_SPECIALTY,
    [specialties]
  );

  const events = useMemo(() => {
    const items = (data?.items || []).filter((i) => !person || i.user_id === person);
    return items.map((i) => ({
      title: i.name,
      start: parseISO(i.start),
      end: parseISO(i.end),
      resource: i,
    }));
  }, [data, person]);

  const eventPropGetter = useCallback(
    (event) => {
      const bg = colorFor(event.resource.specialty);
      return {
        style: {
          backgroundColor: bg,
          color: textColorFor(bg),
          borderColor: "rgba(255,255,255,.4)",
          // An exception day is drawn hatched so it reads differently from the
          // therapist's normal week at a glance.
          backgroundImage: event.resource.is_exception
            ? "repeating-linear-gradient(45deg, rgba(255,255,255,.28), rgba(255,255,255,.28) 5px, transparent 5px, transparent 10px)"
            : "none",
        },
      };
    },
    [colorFor]
  );

  const people = data?.people || [];
  const counts = useMemo(() => {
    const c = {};
    (data?.items || []).forEach((i) => {
      const k = i.specialty || "__none__";
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [data]);

  return (
    <Card className="p-5" data-testid="availability-calendar">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
            <CalendarRange className="h-5 w-5 text-primary" /> Availability calendar
          </h2>
          <p className="text-sm text-muted-foreground">
            Everyone's available hours, coloured by specialty. Hatched blocks are date exceptions.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          <LabeledSelect
            id="cal-specialty"
            value={specialty}
            onChange={(e) => { setSpecialty(e.target.value); setPerson(""); }}
            className="min-w-[180px]"
            aria-label="Filter by specialty"
            data-testid="availability-calendar-specialty"
          >
            <option value="">All specialties</option>
            {Object.entries(specialties).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </LabeledSelect>
          <LabeledSelect
            id="cal-person"
            value={person}
            onChange={(e) => setPerson(e.target.value)}
            className="min-w-[180px]"
            aria-label="Filter by therapist"
            data-testid="availability-calendar-person"
          >
            <option value="">All therapists</option>
            {people.map((p) => (
              <option key={p.user_id} value={p.user_id}>{p.name}</option>
            ))}
          </LabeledSelect>
        </div>
      </div>

      {/* Colour key */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="availability-calendar-legend">
        {Object.entries(specialties).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <span className="h-3 w-3 rounded-[3px]" style={{ backgroundColor: v.color }} />
            {v.label}
            <span className="font-normal text-muted-foreground">({counts[k] || 0})</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground">
          <span className="h-3 w-3 rounded-[3px]" style={{ backgroundColor: NO_SPECIALTY }} />
          No specialty
          <span className="font-normal text-muted-foreground">({counts.__none__ || 0})</span>
        </span>
      </div>

      <div className="emerge-rbc" style={{ height: 560 }}>
        <Calendar
          localizer={localizer}
          events={events}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          onRangeChange={onRangeChange}
          eventPropGetter={eventPropGetter}
          step={30}
          timeslots={2}
          min={new Date(1970, 0, 1, 7, 0)}
          max={new Date(1970, 0, 1, 21, 0)}
          scrollToTime={new Date(1970, 0, 1, 8, 0)}
          popup
          tooltipAccessor={(e) =>
            `${e.resource.name} - ${e.resource.local_start} to ${e.resource.local_end}` +
            (e.resource.is_exception ? ` (exception${e.resource.note ? `: ${e.resource.note}` : ""})` : "")
          }
        />
      </div>

      {events.length === 0 && !isFetching && (
        <p className="mt-4 rounded-[10px] border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          No availability in this period. Set the weekly hours above and it will appear here.
        </p>
      )}
    </Card>
  );
}
